import React, { useEffect, useRef, useState } from "react";
import { claimReward, historyPredictions } from 'service/base';
import { Input, Button, App, Form, Modal, Pagination, Table, Tag } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { SITE } from 'config/config';
import { LocalStorageKey } from 'utils/common/const';
import 'styles/global.css';

const TIMES_FONT_FAMILY = "'Times New Roman', Times, serif";

const HistoryPredictions = ({ isModalVote, setIsModalVote, IP, FP }) => {
  const { notification } = App.useApp();
  const [formHistory] = Form.useForm();
  const [account, setAccount] = useState('');
  const [dataPrediction, setDataPrediction] = useState([]);
  const [isLoadingSubmit, setIsLoadingSubmit] = useState(false);
  const [claimingMatchIds, setClaimingMatchIds] = useState(new Set());

  const autoSubmittedRef = useRef(false);

  const [tableParams] = useState({
    pagination: {
      current: 1,
      pageSize: 15
    }
  });
  const [pagination, setPagination] = useState({
    current: tableParams.pagination.current || 1,
    pageSize: tableParams.pagination.pageSize || 15,
    total: dataPrediction?.totalRecords || 0,
    size: 'default',
    onChange: (page, pageSize) => {
      console.log('Page change:', page, pageSize);
      setPagination({ ...pagination, current: page });
    }
  });

  const handleCancelVote = () => {
    setIsModalVote(false);
  };

  useEffect(() => {
    if (!isModalVote) {
      autoSubmittedRef.current = false;
      return;
    }

    let token = null;
    let username = null;
    try {
      token = localStorage.getItem(LocalStorageKey.ACCESS_TOKEN);
      username = localStorage.getItem('ok9_username');
    } catch {
      // ignore
    }

    console.log('Auto submitting history prediction form with token:', token);
    console.log('Auto submitting history prediction form with account:', username);
    const isSsoLoggedIn = !!(token && username);
    if (!isSsoLoggedIn) return;
    if (autoSubmittedRef.current) return;
    const currentFormAccount = String(formHistory.getFieldValue('account') ?? '').trim();
    if (currentFormAccount) return;

    // Prefill + auto submit once when modal opens
    setAccount(username);
    formHistory.setFieldsValue({ account: username });
    autoSubmittedRef.current = true;
    formHistory.submit();
  }, [isModalVote, formHistory]);

  const onFinishHistoryForm = async (data) => {
    try {
      const accountValue = String(data?.account ?? account ?? '')
        .trim()
        .toLowerCase();
      if (!accountValue) {
        notification.warning({
          message: 'Cảnh báo',
          description: 'Vui lòng nhập tên đang nhập!'
        });
        return;
      }

      let token = null;
      let username = null;
      try {
        token = localStorage.getItem(LocalStorageKey.ACCESS_TOKEN);
        username = localStorage.getItem('ok9_username');
      } catch {
        // ignore
      }
      // const isSsoLoggedIn = !!(token && username);

      // if (!isSsoLoggedIn) {
      //   const confirmed = window.confirm(
      //     'Để đảm bảo tính bảo mật, chúng tôi sẽ sử dụng những dự đoán trước đó dựa trên địa chỉ IP bạn đã truy cập để cung cấp thông tin dự đoán.'
      //   );
      //   if (!confirmed) return;
      // }

      data.account = accountValue;
      setAccount(accountValue);
      setIsLoadingSubmit(true);
      data.site = SITE;
      data.ip = IP;
      data.fp = FP;
      const res = await historyPredictions(data);
      setDataPrediction(res?.data || []);
      if (!res?.data?.listPrediction[0]) {
        notification.warning({
          message: 'Không có lịch sử',
          description: 'Không tìm thấy lịch sử bình chọn, Quý khách vui lòng kiểm tra lại!'
        });
      }
      setIsLoadingSubmit(false);
    } catch (error) {
      setIsLoadingSubmit(false);
    }
  };

  const onFinishHistoryFailed = () => {
    notification.warning({
      message: 'Cảnh báo',
      description: 'Vui lòng kiểm tra lại tài khoản !'
    });
  };

  const handleClaimReward = async (item) => {
    if (item.status_register !== 'apply' || claimingMatchIds.has(item.matchId)) {
      return;
    }
    setClaimingMatchIds((prev) => new Set(prev).add(item.matchId));
    try {
      const accountValue = String(
        item?.account ?? formHistory.getFieldValue('account') ?? account ?? ''
      )
        .trim()
        .toLowerCase();
      if (!accountValue) {
        notification.warning({
          message: 'Thiếu tài khoản',
          description: 'Không xác định được tài khoản để nhận thưởng.'
        });
        return;
      }
      const response = await claimReward({
        account: accountValue,
        site: SITE,
        matchId: item.matchId
      });
      if (response?.code === 0) {
        notification.success({ message: 'Thành công', description: 'Nhận thưởng thành công!' });
        await onFinishHistoryForm({ account: accountValue });
      }
    } catch (error) {
      notification.error({
        message: 'Nhận thưởng thất bại!',
        description: error.message
      });
    } finally {
      setClaimingMatchIds((prev) => {
        const next = new Set(prev);
        next.delete(item.matchId);
        return next;
      });
    }
  };

  let isSsoLoggedIn = false;
  try {
    const token = localStorage.getItem(LocalStorageKey.ACCESS_TOKEN);
    const username = localStorage.getItem('ok9_username');
    isSsoLoggedIn = !!(token && username);
  } catch {
    // ignore
  }

  const columns = [
    {
      title: <span style={{ fontFamily: TIMES_FONT_FAMILY, fontWeight: 700 }}>Trận đấu</span>,
      render: (_, record) => {
        return (
          <span style={{ fontFamily: TIMES_FONT_FAMILY }}>
            {record.homeName} - {record.awayName}
          </span>
        );
      }
    },
    {
      title: <span style={{ fontFamily: TIMES_FONT_FAMILY, fontWeight: 700 }}>Điểm thưởng</span>,
      width: '8%',
      render: (_, record) => (
        <span style={{ fontFamily: TIMES_FONT_FAMILY }}>
          {record.point > 0 ? record.point : '-'}
        </span>
      )
    },
    {
      title: <span style={{ fontFamily: TIMES_FONT_FAMILY, fontWeight: 700 }}>Nhận thưởng</span>,
      width: '16%',
      render: (_, item) => {
        const isClaiming = claimingMatchIds.has(item.matchId);
        const canClaim = item.status_register === 'apply' && !isClaiming;
        return (
          <div className="flex justify-start">
            <button
              type="button"
              onClick={() => handleClaimReward(item)}
              disabled={!canClaim}
              className={`history-claim-btn rounded-[8px] border-none px-2 py-2 text-xs font-semibold text-white md:px-3 md:text-sm ${canClaim ? 'cursor-pointer bg-[#36A638]' : 'cursor-not-allowed bg-[#959FAB]'}`}
            >
              {item.status_register === 'accept'
                ? 'Đã nhận thưởng'
                : isClaiming
                  ? 'Đang xử lý...'
                  : 'Nhận thưởng'}
            </button>
          </div>
        );
      }
    },
    {
      title: <span style={{ fontFamily: TIMES_FONT_FAMILY, fontWeight: 700 }}>Bình chọn</span>,
      width: '15%',
      render: (_, record) => {
        return (
          <Tag
            className="text-center"
            color="#000000"
            key={record._id}
            style={{ fontFamily: TIMES_FONT_FAMILY }}
          >
            {record.homeScore}-{record.awayScore}
          </Tag>
        );
      }
    },
    {
      title: (
        <span style={{ fontFamily: TIMES_FONT_FAMILY, fontWeight: 700 }}>Dự đoán giống bạn</span>
      ),
      width: '15%',
      render: (_, record) => {
        return (
          <Tag
            className="text-center"
            color="#000000"
            key={record._id}
            style={{ fontFamily: TIMES_FONT_FAMILY }}
          >
            {record.sameScore}
          </Tag>
        );
      }
    },

    {
      title: <span style={{ fontFamily: TIMES_FONT_FAMILY, fontWeight: 700 }}>Kết quả</span>,
      width: '8%',
      render: (text, record) => {
        if (record.match_result) {
          return (
            <Tag
              className="text-center"
              color="#00890a"
              key={record._id}
              style={{ fontFamily: TIMES_FONT_FAMILY }}
            >
              {record.match_result}
            </Tag>
          );
        } else {
          return null;
        }
      }
    },
    {
      title: <span style={{ fontFamily: TIMES_FONT_FAMILY, fontWeight: 700 }}>Ngày dự đoán</span>,
      dataIndex: 'createdAt',
      render: (_, record) => (
        <span style={{ fontFamily: TIMES_FONT_FAMILY }}>{dayjs(_).format('DD/MM/YYYY')}</span>
      )
    }
  ];

  const getHistoryRowBg = (index) => ((index + 1) % 2 === 0 ? 'transparent' : '#DCDFE3');

  const desktopColumns = columns.map((column) => ({
    ...column,
    onCell: (_, index) => ({
      style: {
        background: getHistoryRowBg(index),
        color: '#111111',
        fontFamily: TIMES_FONT_FAMILY
      }
    })
  }));

  const predictionRows = dataPrediction?.responseData || [];
  const totalRecords = dataPrediction?.totalRecords ?? predictionRows.length;
  const currentPage = pagination.current || 1;
  const pageSize = pagination.pageSize || 15;
  const mobileStart = (currentPage - 1) * pageSize;
  const mobileRows = predictionRows.slice(mobileStart, mobileStart + pageSize);

  return (
    <Modal
      className="modal-ok9 modal-history"
      title={
        <div className="text-center font-['Times_New_Roman'] text-[16px] md:text-[20px] font-bold tracking-[0.35px] text-[#2B852D]">
          DANH SÁCH LỊCH SỬ DỰ ĐOÁN
        </div>
      }
      open={isModalVote}
      footer={null}
      onCancel={handleCancelVote}
      width={1050}
    >
      <div
        className="content-km content-history rounded-[18px] border border-[#A5E3AD] bg-white p-1 md:p-4"
        style={{ fontFamily: TIMES_FONT_FAMILY }}
      >
        <Form
          name="formHistoryMatch"
          onFinish={onFinishHistoryForm}
          onFinishFailed={onFinishHistoryFailed}
          onValuesChange={(_, allValues) => {
            setAccount(String(allValues?.account ?? '').trim());
          }}
          className="relative"
          autoComplete="off"
          form={formHistory}
          initialValues={{ account: account }}
        >
          <div className="flex justify-center items-start gap-2 md:py-2 py-1 w-full">
            <Form.Item
              name="account"
              className="mb-0 w-full max-w-[272px]"
              rules={[
                {
                  required: true,
                  whitespace: true,
                  message: 'Vui lòng điền tên đăng nhập!'
                }
              ]}
            >
              <Input
                className="times-new-roman-force !h-[36px] md:!h-[42px] w-full !rounded-[40px] !border !border-[#B8BFC7] !bg-white !px-4 font-['Times_New_Roman'] !text-[14px] !font-bold !text-[#4E5F73] placeholder:!text-[#3E4C5C]"
                placeholder="Nhập tên đăng nhập ..."
                maxLength={30}
                style={{
                  fontFamily: "'Times New Roman', Times, serif"
                }}
                prefix={<UserOutlined />}
              />
            </Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={isLoadingSubmit}
              className="times-new-roman-force !h-[36px] md:!h-[42px] !rounded-[80px] !border !border-[rgba(255,255,255,0.5)] !px-5 !font-['Times_New_Roman'] !text-[14px] md:!text-[16px] !font-bold !text-white shadow-[inset_0px_2px_6px_rgba(53,63,55,0.24)]"
              style={{
                background: 'linear-gradient(180deg, #86CA88 0%, #2B852D 100%)'
              }}
            >
              Xác nhận
            </Button>
          </div>
        </Form>
        <div className="list-history mt-3">
          <div className="hidden md:block overflow-x-auto rounded-[14px] p-2">
            <Table
              size="small"
              columns={desktopColumns}
              dataSource={predictionRows}
              loading={isLoadingSubmit}
              rowKey={(record) => record._id ?? `${record.matchId}-${record.account}`}
              rowHoverable={false}
              style={{ fontFamily: TIMES_FONT_FAMILY }}
              className="[&_.ant-table]:!bg-transparent [&_.ant-table-container]:!bg-transparent [&_.ant-table-thead>tr>th]:!bg-[#E6E9ED] [&_.ant-table-thead>tr>th]:!text-[#111111] [&_.ant-table-thead>tr>th]:!font-['Times_New_Roman'] [&_.ant-table-thead>tr>th]:!font-bold [&_.ant-table-thead>tr>th]:!text-[14px] [&_.ant-table-tbody>tr>td]:!border-[#D8DDE2]"
              pagination={{
                current: currentPage,
                pageSize: pageSize,
                total: totalRecords,
                size: 'default',
                position: ['bottomCenter'],
                onChange: (page, nextPageSize) => {
                  setPagination((prev) => ({
                    ...prev,
                    current: page,
                    pageSize: nextPageSize || prev.pageSize
                  }));
                }
              }}
              scroll={{ x: 'max-content' }}
            />
          </div>

          <div className="md:hidden space-y-2 mt-2">
            {mobileRows.map((item) => {
              const isClaiming = claimingMatchIds.has(item.matchId);
              const canClaim = item.status_register === 'apply' && !isClaiming;
              return (
                <div
                  key={item._id ?? `${item.matchId}-${item.account}`}
                  className="rounded-[16px] border border-[#D4EED9] bg-[#ECFFF0] p-3"
                >
                  <div className="font-['Times_New_Roman'] font-bold text-[16px] text-black mb-2">
                    {item.homeName} - {item.awayName}
                  </div>
                  <div className="flex flex-wrap gap-1">
                    <Tag
                      color="#4E5F73"
                      style={{ fontSize: 13, lineHeight: '20px', fontFamily: TIMES_FONT_FAMILY }}
                    >
                      Bình chọn: {item.homeScore}-{item.awayScore}
                    </Tag>
                    <Tag
                      color="#4E5F73"
                      style={{ fontSize: 13, lineHeight: '20px', fontFamily: TIMES_FONT_FAMILY }}
                    >
                      Lượt dự đoán: {item.sameScore}
                    </Tag>
                    {item.match_result ? (
                      <Tag
                        color="#00890a"
                        style={{ fontSize: 13, lineHeight: '20px', fontFamily: TIMES_FONT_FAMILY }}
                      >
                        KQ: {item.match_result}
                      </Tag>
                    ) : (
                      <Tag
                        style={{ fontSize: 13, lineHeight: '20px', fontFamily: TIMES_FONT_FAMILY }}
                      >
                        Chờ KQ
                      </Tag>
                    )}
                    <Tag
                      color="#36a638"
                      style={{ fontSize: 13, lineHeight: '20px', fontFamily: TIMES_FONT_FAMILY }}
                    >
                      Điểm: {item.point > 0 ? item.point : '-'}
                    </Tag>
                    <div className="mt-1 text-[12px] text-gray-500">
                      Ngày dự đoán: {dayjs(item.createdAt).format('DD/MM/YYYY')}
                    </div>
                  </div>
                  <div className="mt-3">
                    <button
                      type="button"
                      onClick={() => handleClaimReward(item)}
                      disabled={!canClaim}
                      className={`history-claim-btn rounded-[8px] border-none px-3 py-2 text-xs font-semibold text-[13px] text-white ${canClaim ? 'cursor-pointer bg-[#36A638]' : 'cursor-not-allowed bg-[#959FAB]'}`}
                      style={{ fontFamily: TIMES_FONT_FAMILY }}
                    >
                      {item.status_register === 'accept'
                        ? 'Đã nhận thưởng'
                        : isClaiming
                          ? 'Đang xử lý...'
                          : 'Nhận thưởng'}
                    </button>
                  </div>
                </div>
              );
            })}

            <Pagination
              current={currentPage}
              pageSize={pageSize}
              total={totalRecords}
              size="small"
              className="!mt-3 text-center"
              onChange={(page, nextPageSize) => {
                setPagination((prev) => ({
                  ...prev,
                  current: page,
                  pageSize: nextPageSize || prev.pageSize
                }));
              }}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default HistoryPredictions;
